import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import moment from "moment";
import {
    FeedbackOverviewUi,
    FeedbackRating,
    MessageFeedbackTableResponse,
    SurveyRatingGroup,
    SurveyTableResponse,
    SurveyTrigger
} from "../../../api/generated";
import {feedbackApi, RequestStatus} from "../../../api/apiConfiguration";
import {RootState} from "../../../app/store";
import {SelectOption} from "../../elements/select/SelectDropdown";

export const FEEDBACK_PAGE_SIZE = 10;

export enum FeedbackTab {
    MESSAGES = "MESSAGES",
    SURVEYS = "SURVEYS"
}

export interface FeedbackFilters {
    startDate: string;
    endDate: string;
    publicBotIdList: string[];
}

export interface MessageFeedbackFilters {
    ratingList: FeedbackRating[];
    withCommentOnly: boolean;
    pageNumber: number;
}

export interface SurveyFilters {
    ratingGroup?: SurveyRatingGroup;
    triggerList: SurveyTrigger[];
    withCommentOnly: boolean;
    pageNumber: number;
}

interface FeedbackState {
    tab: FeedbackTab;
    filters: FeedbackFilters;
    messageFilters: MessageFeedbackFilters;
    surveyFilters: SurveyFilters;
    overview?: FeedbackOverviewUi;
    overviewRequestStatus: RequestStatus;
    messageTable?: MessageFeedbackTableResponse;
    messageTableRequestStatus: RequestStatus;
    surveyTable?: SurveyTableResponse;
    surveyTableRequestStatus: RequestStatus;
}

const getDefaultFilters = (): FeedbackFilters => ({
    startDate: moment().subtract(29, "days").startOf("day").toISOString(),
    endDate: moment().endOf("day").toISOString(),
    publicBotIdList: [],
});

const defaultMessageFilters: MessageFeedbackFilters = {
    ratingList: [],
    withCommentOnly: false,
    pageNumber: 0,
};

const defaultSurveyFilters: SurveyFilters = {
    ratingGroup: undefined,
    triggerList: [],
    withCommentOnly: false,
    pageNumber: 0,
};

const initialState: FeedbackState = {
    tab: FeedbackTab.MESSAGES,
    filters: getDefaultFilters(),
    messageFilters: defaultMessageFilters,
    surveyFilters: defaultSurveyFilters,
    overview: undefined,
    overviewRequestStatus: RequestStatus.NONE,
    messageTable: undefined,
    messageTableRequestStatus: RequestStatus.NONE,
    surveyTable: undefined,
    surveyTableRequestStatus: RequestStatus.NONE,
};

export const fetchFeedbackOverview = createAsyncThunk<FeedbackOverviewUi, undefined, { state: RootState }>(
    "feedback/fetchFeedbackOverview",
    (_payload, {getState}) => {
        return feedbackApi.getFeedbackOverview({
            feedbackOverviewRequest: getState().feedback.filters,
        });
    }
);

export const fetchMessageFeedbackTable = createAsyncThunk<MessageFeedbackTableResponse, undefined, { state: RootState }>(
    "feedback/fetchMessageFeedbackTable",
    (_payload, {getState}) => {
        const {filters, messageFilters} = getState().feedback;
        return feedbackApi.getMessageFeedbackTable({
            messageFeedbackTableRequest: {
                ...filters,
                ...messageFilters,
                pageSize: FEEDBACK_PAGE_SIZE,
            },
        });
    }
);

export const fetchSurveyTable = createAsyncThunk<SurveyTableResponse, undefined, { state: RootState }>(
    "feedback/fetchSurveyTable",
    (_payload, {getState}) => {
        const {filters, surveyFilters} = getState().feedback;
        return feedbackApi.getSurveyTable({
            surveyTableRequest: {
                ...filters,
                ...surveyFilters,
                pageSize: FEEDBACK_PAGE_SIZE,
            },
        });
    }
);

export const feedbackSlice = createSlice({
    name: "feedback",
    initialState,
    reducers: {
        setFeedbackTab: (state, {payload}: PayloadAction<FeedbackTab>) => {
            state.tab = payload;
        },
        updateFeedbackFilters: (state, {payload}: PayloadAction<Partial<FeedbackFilters>>) => {
            state.filters = {...state.filters, ...payload};
            state.messageFilters.pageNumber = 0;
            state.surveyFilters.pageNumber = 0;
        },
        resetFeedbackFilters: (state) => {
            state.filters = getDefaultFilters();
            state.messageFilters = defaultMessageFilters;
            state.surveyFilters = defaultSurveyFilters;
        },
        updateMessageFeedbackFilters: (state, {payload}: PayloadAction<Partial<Omit<MessageFeedbackFilters, "pageNumber">>>) => {
            state.messageFilters = {...state.messageFilters, ...payload, pageNumber: 0};
        },
        setMessageFeedbackPage: (state, {payload}: PayloadAction<number>) => {
            state.messageFilters.pageNumber = payload;
        },
        updateSurveyFilters: (state, {payload}: PayloadAction<Partial<Omit<SurveyFilters, "pageNumber">>>) => {
            state.surveyFilters = {...state.surveyFilters, ...payload, pageNumber: 0};
        },
        setSurveyPage: (state, {payload}: PayloadAction<number>) => {
            state.surveyFilters.pageNumber = payload;
        },
    },
    extraReducers: builder => {
        builder.addCase(fetchFeedbackOverview.pending, (state) => {
            state.overviewRequestStatus = RequestStatus.PENDING;
        });
        builder.addCase(fetchFeedbackOverview.fulfilled, (state, {payload}) => {
            state.overview = payload;
            state.overviewRequestStatus = RequestStatus.RESOLVED;
        });
        builder.addCase(fetchFeedbackOverview.rejected, (state) => {
            state.overviewRequestStatus = RequestStatus.REJECTED;
        });
        builder.addCase(fetchMessageFeedbackTable.pending, (state) => {
            state.messageTableRequestStatus = RequestStatus.PENDING;
        });
        builder.addCase(fetchMessageFeedbackTable.fulfilled, (state, {payload}) => {
            state.messageTable = payload;
            state.messageTableRequestStatus = RequestStatus.RESOLVED;
        });
        builder.addCase(fetchMessageFeedbackTable.rejected, (state) => {
            state.messageTableRequestStatus = RequestStatus.REJECTED;
        });
        builder.addCase(fetchSurveyTable.pending, (state) => {
            state.surveyTableRequestStatus = RequestStatus.PENDING;
        });
        builder.addCase(fetchSurveyTable.fulfilled, (state, {payload}) => {
            state.surveyTable = payload;
            state.surveyTableRequestStatus = RequestStatus.RESOLVED;
        });
        builder.addCase(fetchSurveyTable.rejected, (state) => {
            state.surveyTableRequestStatus = RequestStatus.REJECTED;
        });
    }
});

export const {
    setFeedbackTab,
    updateFeedbackFilters,
    resetFeedbackFilters,
    updateMessageFeedbackFilters,
    setMessageFeedbackPage,
    updateSurveyFilters,
    setSurveyPage,
} = feedbackSlice.actions;

export const feedbackTabSelector = (state: RootState): FeedbackTab => state.feedback.tab;
export const feedbackFiltersSelector = (state: RootState): FeedbackFilters => state.feedback.filters;
export const messageFeedbackFiltersSelector = (state: RootState): MessageFeedbackFilters => state.feedback.messageFilters;
export const surveyFiltersSelector = (state: RootState): SurveyFilters => state.feedback.surveyFilters;
export const feedbackOverviewSelector = (state: RootState): FeedbackOverviewUi | undefined => state.feedback.overview;
export const feedbackOverviewRequestStatusSelector = (state: RootState): RequestStatus => state.feedback.overviewRequestStatus;
export const messageFeedbackTableSelector = (state: RootState): MessageFeedbackTableResponse | undefined => state.feedback.messageTable;
export const messageFeedbackTableRequestStatusSelector = (state: RootState): RequestStatus => state.feedback.messageTableRequestStatus;
export const surveyTableSelector = (state: RootState): SurveyTableResponse | undefined => state.feedback.surveyTable;
export const surveyTableRequestStatusSelector = (state: RootState): RequestStatus => state.feedback.surveyTableRequestStatus;

export const surveyTriggerOptions: SelectOption<SurveyTrigger>[] = [
    {value: SurveyTrigger.EndChat, label: "End of chat"},
    {value: SurveyTrigger.MessageCount, label: "After N messages"},
    {value: SurveyTrigger.Booking, label: "After booking"},
];

export default feedbackSlice.reducer;
